<template>
  <div class="search-rubric-resource-preview">
    <match-indicator v-if="matchTitle" />
    <div class="info-header">{{ title }}</div>

    <div class="rubrics-content">
      <p v-if="hideRubrics">{{ $t("search_rubric_resource_preview_rubric_info_label") }}</p>
      <match-indicator v-if="matchCriterion" />
      <dl class="field-set grading-rubric">
        <grading-rubric-criterion-view
          v-for="(criteria, index) in processedCriteriaList"
          :key="criteria.id"
          :criteria-label="showLabel(index)"
          :criteria-content="criteria"
          :show-mark-range="true"
          :match-band-description="matchBandDescription"
          :visible="true"
          :expanded="false"
        />
      </dl>
    </div>

    <match-indicator v-if="matchNotes" />
    <div class="rubrics-notes">
      <div class="title">
        <svg-icon name="Note24" kind="indicative" />
        <span>{{ $t("rubric_notes_label") }}</span>
      </div>
      <div class="content">
        <empty-placeholder v-if="isNotesEmpty">
          <template #icon>
            <AnnotationCardsEmpty />
          </template>
          {{ $t("search_resource_detail_rubric_notes_placeholder") }}
        </empty-placeholder>
        <rich-text
          v-else
          :key="notes"
          class="description"
          :content="notes"
          :expandable="expandable"
          :expanded="expanded"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { isDefined, isEmpty } from "@utils/var.check";
import { ALPHABETS_LIST } from "@modules/attempt/service/attempt.constant";
import GradingRubricCriterionView from "@modules/editor/components/grading-rubric/GradingRubricCriterionView.vue";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";

export default {
  name: "SearchRubricResourcePreview",
  components: {
    GradingRubricCriterionView,
    AnnotationCardsEmpty,
  },
  props: {
    rubricData: { type: Object, default: null },
    expandable: { type: Boolean, default: true },
    expanded: { type: Boolean, default: true },
  },
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    title() {
      return this.rubricData?.title ?? "";
    },
    notes() {
      return this.isNotesEmpty ? "" : this.rubricData?.notes;
    },
    isNotesEmpty() {
      return isEmpty(this.rubricData?.notes);
    },
    matchTitle() {
      return !isEmpty(this.rubricData?.highlights?.title ?? null);
    },
    matchBandDescription() {
      return !isEmpty(this.rubricData?.highlights?.bandDescription ?? null);
    },
    matchCriterion() {
      return !isEmpty(this.rubricData?.highlights?.criterion ?? null);
    },
    matchNotes() {
      return !isEmpty(this.rubricData?.highlights?.notes ?? null);
    },
    processedCriteriaList() {
      return this.populateCriteriaRangeData();
    },
    hideRubrics() {
      return this.rubricData?.hideRubrics ?? false;
    },
  },
  methods: {
    showLabel(index) {
      return ALPHABETS_LIST[index];
    },
    populateCriteriaRangeData() {
      // Deep cloning here as Object.assign will do shallow clone
      const cloneRubric = isDefined(this.rubricData)
        ? JSON.parse(JSON.stringify(this.rubricData))
        : null;

      if (!(cloneRubric?.rangeList && cloneRubric?.criteriaList && cloneRubric?.criteriaRangeList))
        return {};

      this.populateMinimumScore(cloneRubric);
      this.populateRangeDescription(cloneRubric);
      return this.generateCriteriaRowData(cloneRubric);
    },
    populateMinimumScore(rubric) {
      let previousCellMaxScore = 0;
      let currentRow = 0;

      rubric.criteriaRangeList.forEach((criteriaRange) => {
        // iterate on next criteria and reset max score
        if (currentRow !== criteriaRange.criteriaIndex) {
          previousCellMaxScore = 0;
          currentRow = criteriaRange.criteriaIndex;
        }

        criteriaRange.minScore = criteriaRange.rangeIndex === 0 ? 0 : previousCellMaxScore + 1;
        previousCellMaxScore = criteriaRange.maxScore;
      });
    },
    populateRangeDescription(rubric) {
      // Create a lookup map for rangeList using both id and indexNo as a key
      const rangeIdIndexNoKeyDescriptionMap = new Map();
      rubric.rangeList.forEach((range) => {
        rangeIdIndexNoKeyDescriptionMap.set(`${range.id}_${range.indexNo}`, range.description);
      });

      rubric.criteriaRangeList.forEach((criteriaRange) => {
        const key = `${criteriaRange.gradingRubricRangeId}_${criteriaRange.rangeIndex}`;
        if (rangeIdIndexNoKeyDescriptionMap.has(key)) {
          criteriaRange.headerDescription = rangeIdIndexNoKeyDescriptionMap.get(key);
        }
      });
    },
    generateCriteriaRowData(rubric) {
      const criteriaList = rubric.criteriaList;
      const criteriaRangeList = rubric.criteriaRangeList;

      const criteriaRow = criteriaRangeList.reduce((row, criteriaRange) => {
        row[criteriaRange.criteriaIndex] = [
          ...(row[criteriaRange.criteriaIndex] || []),
          criteriaRange,
        ];
        return row;
      }, []);

      criteriaRow.forEach((row, criteriaIndex) => {
        const matchedCriteria = criteriaList.find((criteria) => criteria.indexNo === criteriaIndex);
        const maxScore = Math.max(...row.map((mark) => mark.maxScore), 0);

        Object.assign(row, {
          criteriaRangesData: [...row], // create a shallow copy
          criteriaDescription: matchedCriteria?.description ?? "",
          criteriaMarks: maxScore,
        });
      });
      return criteriaRow;
    },
  },
};
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-rubric-resource-preview {
  > .info-header {
    @include carbon--type-style("productive-heading-02", true);
    margin-bottom: $spacing-05;
  }

  .rubrics-content {
    padding: $spacing-05;
    margin-bottom: $spacing-05;
    background-color: $ui-01;
    border: 1px solid $ui-03;
    border-radius: 10px;

    > p {
      @include carbon--type-style("body-short-01", true);
      margin-bottom: $spacing-05;
    }

    .match-indicator {
      margin-bottom: $spacing-02;
    }

    .grading-rubric {
      padding: 0;
      .criteria-row {
        display: block;
        .marks {
          @include carbon--type-style("body-short-01", true);
          margin: $spacing-02 0 0 0;
          color: $text-01;
          text-transform: none;
        }
      }
    }

    .bx--content-switcher-btn {
      background-color: $ui-02;
    }

    .bx--content-switcher--selected {
      background-color: $ui-05;
    }

    .criteria-content {
      margin-bottom: 0;
    }
  }

  .rubrics-notes {
    padding: $spacing-05;
    margin-top: $spacing-03;
    background-color: $ui-01;
    border-radius: 10px;

    .title {
      @include carbon--type-style("productive-heading-01", true);
      display: flex;
      align-items: center;
      gap: $spacing-03;
      margin-bottom: $spacing-05;
      color: $text-02;
    }

    .empty-placeholder {
      margin: 0;

      .content {
        margin-bottom: 0;
      }

      .placeholder-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 80px;
        margin: 0;
      }

      svg {
        transform: scale(0.5);
      }
    }
  }
}
</style>
